import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import ChevronRightIcon from "../../../../../../../../assets/svg/chevron-right-icon";
import CompletedIcon from "../../../../../../../../assets/svg/completed-icon";
import CrossIcon from "../../../../../../../../assets/svg/cross-icon";
import LeftArrowIcon from "../../../../../../../../assets/svg/left-arrow-icon";
import LocationIcon from "../../../../../../../../assets/svg/location-icon";
import { ShiftScheduler } from "./AgentDetailAccordions/ShiftScheduler";
import { LeadAssignments } from "./AgentDetailAccordions/LeadAssignments";
import { WhitelistBlacklistManager } from "./AgentDetailAccordions/components/WhitelistBlacklistManager";
import {
  AgentDetailsConfig,
  UtmMediumRestrictionsConfig,
  UtmSourceRestrictionsConfig,
} from "./utils/constants";
import { CountryRestrictionsConfig } from "./utils/constants";
import { CityRestrictionsConfig } from "./utils/constants";
import { AgentDetailsConfigType } from "./utils/types";
import { CountryOption } from "./AgentDetailAccordions/utils/types";
import {
  useGetLeadCitiesQuery,
  useGetLeadCountriesQuery,
  useGetUtmMediumQuery,
  useGetUtmSourcesQuery,
} from "../../../../../../../../redux/services/communicationApi";
import { snakeToTitleCase } from "./utils/functions";
import { FormikProps } from "formik";
import { AgentDetailsValues } from "../../../utils/types";
import { EditUserDetailsValues } from "../../../../EditUserDrawer/utils/types";
import { AgentAvailabilityLeaves } from "./AgentDetailAccordions/components/AgentAvailabilityLeaves/AgentAvailabilityLeaves";
import { useGetAgentBreaksQuery } from "../../../../../../../../redux/services/userManagementApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../redux/store";
import { EDIT_USER_AGENT_DETAILS_HEIGHTS } from "../../../../../../utils/constants";
import { CREATE_USER_AGENT_DETAILS_HEIGHTS } from "../../../../../../utils/constants";

interface AgentDetailsProps {
  isEditUser?: boolean;
  setCreateUserStep?: (step: number) => void;
  formik: FormikProps<AgentDetailsValues> | FormikProps<EditUserDetailsValues>;
}

export interface AgentDetailsRef {
  scrollToFirstError: () => void;
}

export const AgentDetails = forwardRef<AgentDetailsRef, AgentDetailsProps>(
  ({ setCreateUserStep, formik, isEditUser = false }, ref) => {
    const { userIdForEditOrView } = useSelector(
      (state: RootState) => state?.userManagement,
    );
    const fontSize = useSelector(
      (state: RootState) => state?.fontSettings?.fontSize,
    );

    const { data: countryData, isLoading: isCountryLoading } =
      useGetLeadCountriesQuery({});
    const { data: utmSources, isLoading: isUtmSourcesLoading } =
      useGetUtmSourcesQuery({});
    const { data: utmMedium, isLoading: isUtmMediumLoading } =
      useGetUtmMediumQuery({});
    const {
      data: initialAgentBreaksData,
      isLoading: isLoadingAgentBreaksData,
    } = useGetAgentBreaksQuery(userIdForEditOrView || "", {
      skip: !userIdForEditOrView,
      refetchOnMountOrArgChange: true,
    });

    const [expandedId, setExpandedId] = useState<string[]>([]);
    const [agentDetailsItems, setAgentDetailsItems] = useState<
      AgentDetailsConfigType[]
    >(AgentDetailsConfig(isEditUser));

    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const agentDetailsHeight = isEditUser
      ? EDIT_USER_AGENT_DETAILS_HEIGHTS[
          fontSize as keyof typeof EDIT_USER_AGENT_DETAILS_HEIGHTS
        ]
      : CREATE_USER_AGENT_DETAILS_HEIGHTS[
          fontSize as keyof typeof CREATE_USER_AGENT_DETAILS_HEIGHTS
        ];

    const selectedCountryIds = [
      ...(formik?.values?.country_whitelist || []),
      ...(formik?.values?.country_blacklist || []),
    ];

    const finalCountryData = countryData
      ?.filter((country: { name: string }) =>
        selectedCountryIds?.includes(country?.name),
      )
      ?.map((country: { id: string }) => country?.id)
      ?.join(",");

    const { data: cityData, isLoading: isCityLoading } =
      useGetLeadCitiesQuery(finalCountryData);

    const handleBack = () => {
      setCreateUserStep?.(2);
    };

    const handleExpand = (id: string) => {
      setExpandedId((prev) =>
        prev.includes(id) ? prev?.filter((item) => item !== id) : [...prev, id],
      );
    };

    const transformedCountryOptions = useMemo<CountryOption[]>(() => {
      return countryData?.map(
        (country: { id: string; alpha_2: string; name: string }) => ({
          id: country?.id,
          value: country?.name,
          content: country?.name,
          flag: country?.alpha_2,
        }),
      );
    }, [countryData]);

    const transformedCityOptions = useMemo<CountryOption[]>(() => {
      return cityData?.map(
        (city: { id: string; name: string; country_id: string }) => ({
          id: city?.id,
          value: city?.name,
          content: city?.name,
        }),
      );
    }, [cityData]);

    const transformedUtmSourceOptions = useMemo<CountryOption[]>(() => {
      return utmSources?.map((utmSource: { utm_source: string }) => ({
        id: utmSource?.utm_source,
        value: utmSource?.utm_source,
        content: snakeToTitleCase(utmSource?.utm_source),
      }));
    }, [utmSources]);

    const transformedUtmMediumOptions = useMemo<CountryOption[]>(() => {
      return utmMedium?.map((utmMedium: { utm_medium: string }) => ({
        id: utmMedium?.utm_medium,
        value: utmMedium?.utm_medium,
        content: snakeToTitleCase(utmMedium?.utm_medium),
      }));
    }, [utmMedium]);

    const totalLeaves = useMemo(() => {
      return (
        initialAgentBreaksData?.upcoming_breaks?.length +
        initialAgentBreaksData?.previous_breaks?.length
      );
    }, [initialAgentBreaksData]);

    useEffect(() => {
      setAgentDetailsItems((prev) =>
        prev?.map((item) =>
          item?.id === "agent_availability_leaves"
            ? {
                ...item,
                value: totalLeaves > 0 ? totalLeaves + " Upcoming Leaves" : "",
              }
            : item?.id === "countries_allowed_restricted"
              ? {
                  ...item,
                  allowedCount: formik?.values?.country_whitelist?.length,
                  restrictedCount: formik?.values?.country_blacklist?.length,
                }
              : item?.id === "cities_allowed_restricted"
                ? {
                    ...item,
                    allowedCount: formik?.values?.city_whitelist?.length,
                    restrictedCount: formik?.values?.city_blacklist?.length,
                  }
                : item?.id === "utm_sources_allowed_restricted"
                  ? {
                      ...item,
                      allowedCount:
                        formik?.values?.utm_source_whitelist?.length,
                      restrictedCount:
                        formik?.values?.utm_source_blacklist?.length,
                    }
                  : item?.id === "utm_medium_allowed_restricted"
                    ? {
                        ...item,
                        allowedCount:
                          formik?.values?.utm_medium_whitelist?.length,
                        restrictedCount:
                          formik?.values?.utm_medium_blacklist?.length,
                      }
                    : item,
        ),
      );
    }, [formik?.values, totalLeaves]);

    const scrollToFirstError = () => {
      const { errors } = formik;
      const toExpand: string[] = [];

      if (errors?.start_shift || errors?.end_shift)
        toExpand.push("shift_schedule");
      if (
        errors?.velocity ||
        errors?.daily_assignment_limit ||
        errors?.selected_lead_ranges
      )
        toExpand.push("lead_assignment");

      if (toExpand?.length) {
        setExpandedId([toExpand[0]]);

        setTimeout(() => {
          sectionRefs?.current?.[toExpand[0]]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 350);
      }
    };

    useImperativeHandle(ref, () => ({
      scrollToFirstError,
    }));

    return (
      <div className="flex flex-col h-full">
        {!isEditUser && (
          <>
            <button
              onClick={handleBack}
              className="flex items-center gap-1 py-[18px]"
            >
              <LeftArrowIcon className="w-3.5 h-3.5 text-verba-indigo dark:text-verba-indigo-dk" />
              <span className="text-xs font-medium text-heading-text dark:text-heading-text-dk">
                Go Back
              </span>
            </button>
            <div className="text-base text-heading-text dark:text-heading-text-dk mb-[18px]">
              Enter Agent details
            </div>
          </>
        )}
        <div
          className={`flex flex-col gap-y-3 overflow-y-auto no-scrollbar ${agentDetailsHeight}`}
        >
          {agentDetailsItems?.map((item: AgentDetailsConfigType) => (
            <div
              className={`rounded-lg px-3 py-2 ${
                expandedId?.includes(item?.id)
                  ? "border-2 border-selected-option-highlight dark:border-selected-option-highlight-dk"
                  : "border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect hover:dark:bg-hover-effect-dk"
              }`}
              key={item?.id}
              ref={(el) => {
                sectionRefs.current[item?.id] = el;
              }}
            >
              <div
                className="flex items-center justify-between cursor-pointer select-none"
                onClick={() => handleExpand(item?.id)}
              >
                <div className="w-full flex items-center justify-between pr-2">
                  <div className="flex items-center gap-y-3 gap-x-2">
                    <item.icon
                      className={`w-4 h-4 transition-all duration-300 ease-in-out ${
                        expandedId?.includes(item?.id)
                          ? "text-verba-blue dark:text-verba-blue-dk"
                          : "text-verba-indigo dark:text-verba-indigo-dk"
                      } ${item?.iconClassName}`}
                    />
                    <div className="flex flex-col">
                      <div className="text-sm leading-[18px] font-medium text-verba-field-label dark:text-verba-field-label-dk">
                        {item?.header}
                      </div>
                      <div className="text-xs leading-[15px] text-dark-gray dark:text-dark-gray-dk">
                        {item?.subHeader}
                      </div>
                    </div>
                  </div>
                  {item?.value && (
                    <span className="h-[18px] px-1 flex items-center justify-center text-xxs font-semibold leading-0 text-verba-indigo dark:text-verba-indigo-dk rounded-sm border border-gray-100 dark:border-gray-100-dk">
                      {item?.value}
                    </span>
                  )}
                  {((item?.allowedCount ?? 0) > 0 ||
                    (item?.restrictedCount ?? 0) > 0) && (
                    <div className="flex items-center gap-x-1">
                      {(item?.allowedCount ?? 0) > 0 && (
                        <span className="h-[18px] flex items-center justify-center gap-x-0.5 px-1 text-xxs font-semibold leading-0 text-verba-indigo dark:text-verba-indigo-dk rounded-sm border border-gray-100 dark:border-gray-100-dk">
                          <CompletedIcon className="w-2.5 h-2.5 text-verba-green-success dark:text-verba-green-success-dk" />
                          <span>{item?.allowedCount}</span>
                        </span>
                      )}
                      {(item?.restrictedCount ?? 0) > 0 && (
                        <span className="h-[18px] flex items-center justify-center gap-x-0.5 px-1 text-xxs font-semibold leading-0 text-verba-indigo dark:text-verba-indigo-dk rounded-sm border border-gray-100 dark:border-gray-100-dk">
                          <CrossIcon className="w-2.5 h-2.5 text-verba-red-alert dark:text-verba-red-alert-dk" />
                          <span>{item?.restrictedCount}</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <ChevronRightIcon
                  className={`w-6 h-6 transition-all duration-300 ease-in-out ${
                    expandedId?.includes(item?.id)
                      ? "text-verba-blue dark:text-verba-blue-dk -rotate-90"
                      : "text-verba-indigo dark:text-verba-indigo-dk rotate-90"
                  }`}
                />
              </div>
              {expandedId?.includes(item?.id) && (
                <>
                  <div className="border-b border-border-gray dark:border-border-gray-dk my-3.5" />
                  {item?.id === "shift_schedule" && (
                    <ShiftScheduler formik={formik} />
                  )}
                  {item?.id === "agent_availability_leaves" && isEditUser && (
                    <AgentAvailabilityLeaves
                      initialAgentBreaksData={initialAgentBreaksData}
                      isLoadingAgentBreaksData={isLoadingAgentBreaksData}
                    />
                  )}
                  {item?.id === "lead_assignment" && (
                    <LeadAssignments formik={formik} />
                  )}
                  {item?.id === "countries_allowed_restricted" && (
                    <WhitelistBlacklistManager
                      formik={formik}
                      whitelistName="country_whitelist"
                      blacklistName="country_blacklist"
                      isFlagIcon
                      config={CountryRestrictionsConfig}
                      transformedOptions={transformedCountryOptions}
                      isLoading={isCountryLoading}
                    />
                  )}
                  {item?.id === "cities_allowed_restricted" && (
                    <WhitelistBlacklistManager
                      formik={formik}
                      whitelistName="city_whitelist"
                      blacklistName="city_blacklist"
                      Icon={LocationIcon}
                      iconClassName="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk"
                      config={CityRestrictionsConfig}
                      transformedOptions={transformedCityOptions}
                      isLoading={isCityLoading}
                    />
                  )}
                  {item?.id === "utm_sources_allowed_restricted" && (
                    <WhitelistBlacklistManager
                      formik={formik}
                      whitelistName="utm_source_whitelist"
                      blacklistName="utm_source_blacklist"
                      config={UtmSourceRestrictionsConfig}
                      transformedOptions={transformedUtmSourceOptions}
                      isLoading={isUtmSourcesLoading}
                    />
                  )}
                  {item?.id === "utm_medium_allowed_restricted" && (
                    <WhitelistBlacklistManager
                      formik={formik}
                      whitelistName="utm_medium_whitelist"
                      blacklistName="utm_medium_blacklist"
                      config={UtmMediumRestrictionsConfig}
                      transformedOptions={transformedUtmMediumOptions}
                      isLoading={isUtmMediumLoading}
                    />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  },
);
